/**
 * @file useModerationOverview.js
 * @brief Loads aggregated moderation overview metrics and recent activity.
 */
import { useCallback, useEffect, useState } from "react";

const TEMPORAL_DAYS = 14;

function formatDayKey(date) {
    return date.toISOString().slice(0, 10);
}

function buildEmptyTemporalSeries(days = TEMPORAL_DAYS) {
    const now = new Date();
    const dates = [];

    for (let offset = days - 1; offset >= 0; offset -= 1) {
        const day = new Date(now);
        day.setHours(0, 0, 0, 0);
        day.setDate(day.getDate() - offset);
        dates.push({
            date: formatDayKey(day),
            pending: 0,
            active: 0,
            rejected: 0,
        });
    }

    return dates;
}

/**
 * Fetch moderation overview counters and recent activity snippets.
 * @param {Object} params Hook parameters.
 * @param {Object} params.api API client used for overview requests.
 * @param {boolean} params.enabled Whether overview loading should run.
 * @returns {{loading: boolean, error: Error|null, overview: Object, refresh: Function}} Overview state and refresh handler.
 */
export default function useModerationOverview({ api, enabled }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [overview, setOverview] = useState({
        pendingCount: 0,
        rejectedCount: 0,
        activeCount: 0,
        activeBeforeWindow: 0,
        usersCount: 0,
        recentPending: [],
        temporalSeries: buildEmptyTemporalSeries(),
        temporalDays: TEMPORAL_DAYS,
    });

    const refresh = useCallback(async () => {
        if (!enabled) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await api.get("/listings/moderation-overview/");
            const data = res?.data || {};

            const temporalDays = Number(data.temporal_days || TEMPORAL_DAYS);
            const temporalSeries = Array.isArray(data.temporal_series)
                ? data.temporal_series
                : buildEmptyTemporalSeries(temporalDays);

            setOverview({
                pendingCount: Number(data.pending_count || 0),
                rejectedCount: Number(data.rejected_count || 0),
                activeCount: Number(data.active_count || 0),
                activeBeforeWindow: Number(data.active_before_window || 0),
                usersCount: Number(data.users_count || 0),
                recentPending: Array.isArray(data.recent_pending) ? data.recent_pending : [],
                temporalSeries,
                temporalDays,
            });
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [api, enabled]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        refresh();
    }, [enabled, refresh]);

    return {
        loading,
        error,
        overview,
        refresh,
    };
}
